import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import {
  ChartBarIcon,
  UserGroupIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CogIcon,
  ServerIcon,
  ChatBubbleLeftIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

interface AdminOverview {
  totalUsers: number;
  totalSessions: number;
  activeSessions: number;
  totalChatters: number;
  activeWebSocketConnections: number;
  activeTmiConnections: number;
}

interface AdminSession {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  startedAt: string;
  endedAt?: string;
  user: {
    id: string;
    username: string;
    displayName: string;
    twitchId: string;
  };
  _count: {
    chatters: number;
  };
}

interface AdminUser {
  id: string;
  username: string;
  displayName: string;
  twitchId: string;
  email?: string;
  isApproved: boolean;
  isAdmin: boolean;
  createdAt: string;
  sessions: AdminSession[];
  _count: {
    sessions: number;
    chatters: number;
  };
}

interface SystemStatus {
  websocketConnections: number;
  activeTmiConnections: number;
  activeMonitors: string[];
  uptime: number;
  memoryUsage: {
    rss: number;
    heapTotal: number;
    heapUsed: number;
    external: number;
  };
  timestamp: string;
}

const AdminPage: React.FC = () => {
  const { user } = useAuthStore();
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'sessions' | 'users' | 'system'>('overview');
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    type: 'force-stop-session' | 'force-logout-user' | 'delete-user';
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    type: 'force-stop-session',
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Check if user is admin
  const isAdmin = user?.isAdmin;

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        loadOverview(),
        loadSessions(),
        loadUsers(),
        loadSystemStatus()
      ]);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Admin access required');
      return;
    }
    loadData();
  }, [isAdmin, loadData]);

  const loadOverview = async () => {
    const response = await api.get('/api/admin/overview');
    setOverview(response.data);
  };

  const loadSessions = async () => {
    const response = await api.get('/api/admin/sessions');
    setSessions(response.data);
  };

  const loadUsers = async () => {
    const response = await api.get('/api/admin/users');
    setUsers(response.data);
  };

  const loadSystemStatus = async () => {
    const response = await api.get('/api/admin/system-status');
    setSystemStatus(response.data);
  };

  const handleForceStopSession = async (sessionId: string, sessionName: string) => {
    try {
      await api.post(`/api/admin/sessions/${sessionId}/force-stop`);
      toast.success(`Session "${sessionName}" force stopped successfully`);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error force stopping session:', error);
      toast.error('Failed to force stop session');
    }
  };

  const handleForceLogoutUser = async (userId: string, username: string) => {
    try {
      await api.post(`/api/admin/users/${userId}/force-logout`);
      toast.success(`User "${username}" force logged out successfully`);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error force logging out user:', error);
      toast.error('Failed to force logout user');
    }
  };

  const handleApproveUser = async (userId: string, username: string) => {
    try {
      await api.post(`/api/admin/users/${userId}/approve`);
      toast.success(`User "${username}" approved successfully`);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error('Failed to approve user');
    }
  };

  const handleRejectUser = async (userId: string, username: string) => {
    try {
      await api.post(`/api/admin/users/${userId}/reject`);
      toast.success(`User "${username}" rejected successfully`);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error rejecting user:', error);
      toast.error('Failed to reject user');
    }
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    try {
      await api.delete(`/api/admin/users/${userId}`);
      toast.success(`User "${username}" deleted successfully`);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handlePromoteToAdmin = async (userId: string, username: string) => {
    try {
      await api.post(`/api/admin/users/${userId}/promote`);
      toast.success(`User "${username}" promoted to admin successfully`);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error promoting user to admin:', error);
      toast.error('Failed to promote user to admin');
    }
  };

  const handleDemoteFromAdmin = async (userId: string, username: string) => {
    try {
      await api.post(`/api/admin/users/${userId}/demote`);
      toast.success(`User "${username}" demoted from admin successfully`);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error demoting user from admin:', error);
      toast.error('Failed to demote user from admin');
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-gray-400">Admin access required</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="px-6 lg:px-12 py-8 flex-1">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white light:text-gray-900 mb-2">Admin Panel</h1>
          <p className="text-gray-400 light:text-gray-600">System administration and monitoring</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: ChartBarIcon },
              { id: 'sessions', name: 'Sessions', icon: PlayIcon },
              { id: 'users', name: 'Users', icon: UserGroupIcon },
              { id: 'system', name: 'System', icon: ServerIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-twitch-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && overview && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="card p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserGroupIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">Total Users</dt>
                      <dd className="text-lg font-medium text-white light:text-gray-900">{overview.totalUsers}</dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <PlayIcon className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">Active Sessions</dt>
                      <dd className="text-lg font-medium text-white light:text-gray-900">{overview.activeSessions}</dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChatBubbleLeftIcon className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">Total Chatters</dt>
                      <dd className="text-lg font-medium text-white light:text-gray-900">{overview.totalChatters}</dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ServerIcon className="h-8 w-8 text-orange-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">TMI Connections</dt>
                      <dd className="text-lg font-medium text-white light:text-gray-900">{overview.activeTmiConnections}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-white light:text-gray-900 mb-4">Quick Actions</h3>
              <div className="flex space-x-4">
                <button
                  onClick={loadData}
                  className="btn-secondary"
                >
                  <CogIcon className="h-4 w-4 mr-2" />
                  Refresh Data
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="space-y-6">
            <div className="card p-6">
              <h3 className="text-lg font-medium text-white light:text-gray-900 mb-4">All Sessions</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Session</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Chatters</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {sessions.map((session) => (
                      <tr key={session.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white light:text-gray-900">{session.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-300">{session.user.displayName}</div>
                          <div className="text-sm text-gray-500">@{session.user.username}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            session.isActive
                              ? 'bg-green-900 text-green-300'
                              : 'bg-gray-700 text-gray-300'
                          }`}>
                            {session.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {session._count.chatters}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {session.isActive && (
                            <button
                              onClick={() => {
                                setConfirmDialog({
                                  isOpen: true,
                                  type: 'force-stop-session',
                                  title: 'Force Stop Session',
                                  message: `Are you sure you want to force stop the session "${session.name}"?`,
                                  onConfirm: () => handleForceStopSession(session.id, session.name)
                                });
                              }}
                              className="text-red-400 hover:text-red-300"
                            >
                              <StopIcon className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="card p-6">
              <h3 className="text-lg font-medium text-white light:text-gray-900 mb-4">All Users</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Twitch ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Admin</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Sessions</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Chatters</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white light:text-gray-900">{user.displayName}</div>
                          <div className="text-sm text-gray-500">@{user.username}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {user.twitchId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.isApproved
                              ? 'bg-green-900 text-green-300'
                              : 'bg-yellow-900 text-yellow-300'
                          }`}>
                            {user.isApproved ? 'Approved' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.isAdmin
                              ? 'bg-purple-900 text-purple-300'
                              : 'bg-gray-700 text-gray-300'
                          }`}>
                            {user.isAdmin ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {user._count.sessions}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {user._count.chatters}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {!user.isApproved ? (
                              <button
                                onClick={() => handleApproveUser(user.id, user.displayName)}
                                className="text-green-400 hover:text-green-300"
                                title="Approve user"
                              >
                                <CheckIcon className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleRejectUser(user.id, user.displayName)}
                                className="text-yellow-400 hover:text-yellow-300"
                                title="Reject user"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            )}
                            {!user.isAdmin ? (
                              <button
                                onClick={() => handlePromoteToAdmin(user.id, user.displayName)}
                                className="text-purple-400 hover:text-purple-300"
                                title="Promote to admin"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                </svg>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleDemoteFromAdmin(user.id, user.displayName)}
                                className="text-gray-400 hover:text-gray-300"
                                title="Demote from admin"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setConfirmDialog({
                                  isOpen: true,
                                  type: 'force-logout-user',
                                  title: 'Force Logout User',
                                  message: `Are you sure you want to force logout "${user.displayName}"? This will invalidate their tokens and disconnect all their sessions.`,
                                  onConfirm: () => handleForceLogoutUser(user.id, user.displayName)
                                });
                              }}
                              className="text-orange-400 hover:text-orange-300"
                              title="Force logout user"
                            >
                              <StopIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setConfirmDialog({
                                  isOpen: true,
                                  type: 'delete-user',
                                  title: 'Delete User',
                                  message: `Are you sure you want to permanently delete "${user.displayName}"? This will remove all their data including sessions, chatters, and settings. This action cannot be undone.`,
                                  onConfirm: () => handleDeleteUser(user.id, user.displayName)
                                });
                              }}
                              className="text-red-400 hover:text-red-300"
                              title="Delete user permanently"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'system' && systemStatus && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card p-6">
                <h3 className="text-lg font-medium text-white light:text-gray-900 mb-4">Connection Status</h3>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-400">WebSocket Connections</dt>
                    <dd className="text-sm font-medium text-white">{systemStatus.websocketConnections}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-400">TMI Connections</dt>
                    <dd className="text-sm font-medium text-white">{systemStatus.activeTmiConnections}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-400">Uptime</dt>
                    <dd className="text-sm font-medium text-white">{formatUptime(systemStatus.uptime)}</dd>
                  </div>
                </dl>
              </div>

              <div className="card p-6">
                <h3 className="text-lg font-medium text-white light:text-gray-900 mb-4">Memory Usage</h3>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-400">RSS</dt>
                    <dd className="text-sm font-medium text-white">{formatBytes(systemStatus.memoryUsage.rss)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-400">Heap Total</dt>
                    <dd className="text-sm font-medium text-white">{formatBytes(systemStatus.memoryUsage.heapTotal)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-400">Heap Used</dt>
                    <dd className="text-sm font-medium text-white">{formatBytes(systemStatus.memoryUsage.heapUsed)}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-medium text-white light:text-gray-900 mb-4">Active Monitors</h3>
              {systemStatus.activeMonitors.length > 0 ? (
                <div className="space-y-2">
                  {systemStatus.activeMonitors.map((monitor, index) => (
                    <div key={index} className="text-sm text-gray-300 bg-gray-700 px-3 py-2 rounded">
                      {monitor}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">No active monitors</p>
              )}
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-medium text-white light:text-gray-900 mb-4">System Info</h3>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-400">Last Updated</dt>
                  <dd className="text-sm font-medium text-white">
                    {new Date(systemStatus.timestamp).toLocaleString()}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Confirm"
        cancelText="Cancel"
      />

      <Footer />
    </>
  );
};

export default AdminPage; 